/**
 * Soft-keyboard signal, kept in its own module so a component can subscribe
 * without importing `bootstrap.ts` — that pulls in @capacitor/app,
 * splash-screen, status-bar, live-activity and iap, none of which a bar that
 * wants to know the keyboard height has any use for.
 *
 * Fired whenever the keyboard's height changes, with `{ height }` in px.
 * `bootstrap.ts` is the only publisher.
 *
 * 🚨 Subscribe to this rather than to `visualViewport` directly. visualViewport
 * is silent in WKWebView under `KeyboardResize.None` — which is what
 * capacitor.config.ts sets — so on iOS a visualViewport-only listener never
 * hears the keyboard at all. This event is fed by the Capacitor Keyboard plugin
 * on native and visualViewport on web, so it works on every surface.
 *
 * The matching CSS hooks, written by the same publisher, are the
 * `--mucka-keyboard-h` custom property and the `keyboard-open` class on
 * `<html>`. Prefer those when restyling is all you need; use this event when
 * something has to actually run.
 */
export const KEYBOARD_EVENT = 'mucka-keyboard';

export type KeyboardEventDetail = { height: number };

export interface KeyboardTracker {
  /** Capacitor Keyboard plugin height. Native only. */
  setPlugin(height: number): void;
  /** visualViewport-derived inset. Web, and native as a fallback. */
  setViewport(height: number): void;
}

/**
 * Combines the two keyboard-height sources into one value.
 *
 * 🚨 They are combined with `max()`, and must NOT be collapsed back into a
 * single shared setter. Last-writer-wins is what broke the Mucka Bar on
 * Android: the Capacitor plugin reports the real height, then the WebView
 * reflows for the keyboard, which fires visualViewport — whose
 * `innerHeight - vv.height` is ~0 on an already-reflowed viewport. That 0
 * overwrote the plugin's height a frame later and stripped `keyboard-open`,
 * so the nav tabs reappeared over the keyboard.
 *
 * iOS hid the bug completely: under `KeyboardResize.None` the viewport never
 * reflows, so visualViewport never fires and never clobbers anything. Any fix
 * verified on iOS alone will therefore look correct while Android stays broken
 * — test both.
 *
 * `max()` preserves the documented fallback (a plugin that under-reports still
 * loses to a visualViewport that doesn't) while stopping a source that reports
 * 0 from vetoing one reporting a real height. Each source keeps its own slot,
 * so "closed" is both slots at zero rather than a high-water mark that never
 * comes down.
 *
 * `onChange` fires only when the combined height actually changes —
 * visualViewport also emits `scroll`, which would otherwise re-broadcast the
 * same height on every flick.
 */
export function createKeyboardTracker(onChange: (height: number) => void): KeyboardTracker {
  let plugin = 0;
  let viewport = 0;
  // Starts at 0, not -1: the tracker's initial state IS "keyboard closed", so a
  // source reporting 0 before anything has opened is confirming that, not
  // changing it. Seeding -1 made the first hide event — or a negative
  // visualViewport reading mid-rotation — broadcast a phantom "keyboard just
  // closed" to every subscriber at boot.
  let last = 0;

  const apply = () => {
    const height = Math.max(plugin, viewport);
    if (height === last) return;
    last = height;
    onChange(height);
  };

  return {
    setPlugin(height: number) {
      plugin = Math.max(0, height) || 0;
      apply();
    },
    setViewport(height: number) {
      viewport = Math.max(0, height) || 0;
      apply();
    },
  };
}
